import { useEffect, useState } from 'react';
import { api, type CatalogMeta } from '../api/client.js';
import { FilterEditor } from '../components/FilterEditor.js';
import { FilterPreview } from '../components/FilterPreview.js';
import { FilterSummary } from '../components/FilterSummary.js';
import { ParticipantList } from '../components/ParticipantList.js';
import { QrCode } from '../components/QrCode.js';
import { MatchRuleSetting } from '../components/MatchRuleSetting.js';
import { TimerSetting } from '../components/TimerSetting.js';
import { AddFilmModal } from '../components/AddFilmModal.js';
import { COMMUNITY_CATALOG } from '../lib/features.js';
import { ConfirmButton } from '../components/ConfirmButton.js';
import { useT } from '../i18n/index.js';
import { copyText, isLocalhostOrigin } from '../lib/clipboard.js';
import { canShare, share } from '../lib/share.js';
import { useFilterPreview } from '../session/useFilterPreview.js';
import { useSession } from '../session/useSession.js';

/** Waiting room: share the code, watch people arrive, tune the filters. */
export function LobbyScreen() {
  const t = useT();
  const {
    state,
    credentials,
    isHost,
    start,
    updateFilters,
    setMatchRule,
    setTimer,
    removeParticipant,
    transferHost,
    leave,
  } = useSession();
  const [editing, setEditing] = useState(false);
  const [meta, setMeta] = useState<CatalogMeta | null>(null);
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);
  const [copyFailed, setCopyFailed] = useState(false);

  // Only while the editor is open: outside it the filters cannot change, and
  // `state.deckSize` already says how big the deck is.
  const preview = useFilterPreview(
    state?.filters ?? { providers: [], genres: [], maxRuntimeMinutes: null, country: 'DE' },
    editing && isHost,
  );

  useEffect(() => {
    if (!editing || meta) return;
    api
      .catalogMeta()
      .then(setMeta)
      .catch(() => setMeta(null));
  }, [editing, meta]);

  if (!state || !credentials) return null;

  const inviteUrl = `${window.location.origin}${window.location.pathname}?code=${state.code}`;

  const flashCopied = (what: 'code' | 'link') => {
    setCopied(what);
    window.setTimeout(() => setCopied(null), 1500);
  };

  const copy = async (value: string, what: 'code' | 'link') => {
    const ok = await copyText(value);
    setCopyFailed(!ok);
    if (ok) flashCopied(what);
  };

  // One button that does the right thing per device: the system share sheet
  // where there is one — straight into the group chat — and the clipboard
  // where there is not.
  const invite = async () => {
    const outcome = await share({
      title: t('lobby.inviteTitle'),
      text: t('lobby.inviteText', { code: state.code }),
      url: inviteUrl,
    });
    setCopyFailed(outcome === 'failed');
    if (outcome === 'copied') flashCopied('link');
  };

  const host = state.participants.find((participant) => participant.id === state.hostId);
  const activeCount = state.participants.filter((participant) => !participant.left).length;

  const inviteBlock = (
    <div className="stack">
      <span className="field__label">{t('lobby.shareCode')}</span>
      <div className="code-display">{state.code}</div>
      <div className="lobby__share">
        <button type="button" className="btn btn--block" onClick={() => void invite()}>
          {copied === 'link'
            ? t('common.copied')
            : canShare()
              ? t('lobby.invite')
              : t('lobby.shareLink')}
        </button>
        <button
          type="button"
          className="btn btn--block"
          onClick={() => void copy(state.code, 'code')}
        >
          {copied === 'code' ? t('common.copied') : t('lobby.copyCode')}
        </button>
      </div>

      {/* The fastest way into a session for anybody in the same room: hold
          the phone up, the other one points its camera at it, done. */}
      <div className="lobby__qr">
        <QrCode text={inviteUrl} label={t('lobby.qrLabel', { code: state.code })} />
        <span className="muted small center">{t('lobby.qrHint')}</span>
      </div>

      {/* The link as text only when the clipboard refused: then it is the way
          to copy it by hand. Otherwise it is just the button above, again. */}
      {copyFailed ? (
        <>
          <p className="banner banner--warn small">{t('lobby.copyFailed')}</p>
          <code className="invite-link">{inviteUrl}</code>
        </>
      ) : null}
      {isLocalhostOrigin() ? (
        <p className="banner banner--warn small">{t('lobby.localhostHint')}</p>
      ) : null}
    </div>
  );

  const peopleBlock = (
    <div className="stack stack--tight">
      <span className="field__label">
        {t('lobby.people')} · {activeCount}
      </span>
      <div className="panel panel--flush">
        <ParticipantList
          participants={state.participants}
          meId={credentials.participantId}
          hostActions={
            isHost
              ? {
                  onRemove: (id) => void removeParticipant(id),
                  onMakeHost: (id) => void transferHost(id),
                }
              : undefined
          }
        />
      </div>
      {isHost && activeCount < 2 ? (
        <span className="muted small">{t('lobby.aloneHint')}</span>
      ) : null}
    </div>
  );

  return (
    <div className="screen">
      <div className="row row--between">
        <h1>{t('lobby.title')}</h1>
        <ConfirmButton
          className="btn btn--ghost btn--small"
          label={t('swipe.leaveShort')}
          confirmLabel={t('swipe.leaveConfirm')}
          onConfirm={() => void leave()}
        />
      </div>

      {/* The host's job here is to get people in, so the invite comes first.
          A guest is already in: what they want to see is who else is, and
          that everybody is waiting on the host — not the invite they just
          used, which moves to the bottom. */}
      {isHost ? (
        <>
          {inviteBlock}
          {peopleBlock}
        </>
      ) : (
        <>
          <div className="panel lobby__waiting" role="status">
            <span className="lobby__pulse" aria-hidden="true" />
            <span>{t('lobby.waitingForHost', { name: host?.name ?? '' })}</span>
          </div>
          {peopleBlock}
        </>
      )}

      <div className="stack stack--tight">
        <div className="row row--between">
          <span className="field__label">{t('lobby.filters')}</span>
          {isHost ? (
            <button
              type="button"
              className="btn btn--ghost btn--small"
              onClick={() => setEditing((value) => !value)}
            >
              {editing ? t('common.close') : t('lobby.editFilters')}
            </button>
          ) : null}
        </div>

        {editing && meta ? (
          <FilterEditor
            meta={meta}
            filters={state.filters}
            onChange={(filters) => void updateFilters(filters)}
          />
        ) : (
          <FilterSummary filters={state.filters} />
        )}
        {editing && preview ? (
          <FilterPreview preview={preview} />
        ) : (
          <span className="muted small">{t('lobby.deckSize', { count: state.deckSize })}</span>
        )}
        {/* Next to the deck it adds to, rather than in the header where it
            competed with leaving. Adding a film by hand belongs to the
            community catalog; with the catalog coming from TMDB there is
            nothing to add it to. */}
        {COMMUNITY_CATALOG ? (
          <div>
            <AddFilmModal />
          </div>
        ) : null}
      </div>

      <div className="stack stack--tight">
        <span className="field__label">{t('matchRule.label')}</span>
        <MatchRuleSetting
          rule={state.settings.matchRule}
          canEdit={isHost}
          onChange={(rule) => void setMatchRule(rule)}
        />
      </div>

      <div className="stack stack--tight">
        <span className="field__label">{t('timer.label')}</span>
        <TimerSetting
          minutes={state.settings.runoffTimerMinutes}
          canEdit={isHost}
          onChange={(minutes) => void setTimer(minutes)}
        />
      </div>

      {isHost ? null : (
        <details className="panel lobby__more">
          <summary>{t('lobby.inviteMore')}</summary>
          {inviteBlock}
        </details>
      )}

      <span className="spacer" />

      {isHost ? (
        <div className="sticky-cta">
          <button
            type="button"
            className="btn btn--primary btn--lg btn--block"
            onClick={() => void start()}
            disabled={state.deckSize === 0}
          >
            {t('lobby.start')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
